import { GetServerSideProps } from 'next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { InstantSearchServerState } from 'react-instantsearch'
import * as yup from 'yup'
import Layout from 'components/Layout'
import { searchIndex } from 'containers/meilisearchClient'
import SeoPages from 'containers/seoPages'
import Meta from 'containers/seoPages/Meta'
import { generateServerStateFromProps } from 'containers/seoPages/generateServerStateFromProps'
import defaultStartDate from 'domain/constants/EditionLiveStartDateTimestamp'
import {
  getBaseUrl,
  getParsedData,
  LocationType,
  queryLocationSchema,
  queryTypeOrDistanceSchema,
  TypeOrDistanceType
} from 'helpers/index'
import { liveRateRepository } from 'live/liveInstances'
import i18nConfig from 'next-i18next.config'
import EventService, {
  Lvl1RegionEvents
} from 'services/api/database/EventService'
import { defaultFrontLocales } from 'utils/locale'

const querySchema = yup
  .object()
  .shape({
    page: yup.number()
  })
  .concat(queryLocationSchema)
  .concat(queryTypeOrDistanceSchema)

export type EventByRegion = Record<string, Lvl1RegionEvents>

interface PageProps {
  attributesToRetrieve: string[]
  serverState?: InstantSearchServerState
  filters: string
  typeOrDistanceKey: string | null
  parsedLocation?: string
  typeOrDistance: TypeOrDistanceType
  page?: number
  location?: LocationType
  results: {
    resultsByLvl1: EventByRegion
    resultsByLvl2: EventByRegion
    resultsByDepartment: EventByRegion
  }
  currencyExchangeRates: Record<string, number>
}

function Page({
  parsedLocation,
  attributesToRetrieve,
  typeOrDistanceKey,
  filters,
  serverState,
  typeOrDistance,
  page,
  location,
  results,
  currencyExchangeRates
}: PageProps) {
  return (
    <>
      <Meta
        typeOrDistanceKey={typeOrDistanceKey}
        parsedLocation={parsedLocation}
        serverState={serverState}
      />
      <Layout>
        <SeoPages
          attributesToRetrieve={attributesToRetrieve}
          serverState={serverState}
          typeOrDistance={typeOrDistance}
          page={page}
          resultsByRegion={results}
          filters={filters}
          location={location}
          currencyExchangeRates={currencyExchangeRates}
        />
      </Layout>
    </>
  )
}

const eventService = new EventService()
export const getServerSideProps: GetServerSideProps<PageProps> = async ({
  locale,
  query,
  res
}) => {
  const isValid = querySchema.isValidSync(query)
  if (!isValid) {
    return {
      notFound: true
    }
  }
  const { page, typeOrDistance, location } = query

  const {
    parsedTypeOrDistance,
    typeOrDistanceKey,
    parsedLocation,
    typeOrDistanceAlgoliaTranslation
  } = getParsedData({
    typeOrDistance,
    location
  })
  const distanceFilter = typeOrDistanceAlgoliaTranslation
    ? `editionLiveLevel2CategoryTranslation="${typeOrDistanceAlgoliaTranslation}" OR editionLiveLevel1CategoryTranslation="${typeOrDistanceAlgoliaTranslation}"`
    : ''
  const locationFilter = `eventLocationData="${parsedLocation}"`
  const filters = !parsedTypeOrDistance
    ? `${locationFilter} AND editionLiveEndDateTimestamp>${defaultStartDate} AND eventStatus="LIVE"`
    : `(${distanceFilter}) AND ${locationFilter} AND editionLiveEndDateTimestamp>${defaultStartDate} AND eventStatus="LIVE"`

  if (page === 1) {
    return {
      redirect: {
        destination: `${getBaseUrl(locale)}/${typeOrDistance}/${location}`,
        permanent: true
      }
    }
  }

  const dataEventsByRegion = (
    await eventService.getLvl1RegionEvents({
      location: parsedLocation
    })
  ).sort()

  const results = {
    resultsByLvl1: dataEventsByRegion.reduce<EventByRegion>(function (r, a) {
      r[a.categoryLevel1] = r[a.categoryLevel1] || []
      r[a.categoryLevel1]?.push(a)
      return r
    }, {}),

    resultsByLvl2: dataEventsByRegion.reduce<EventByRegion>(function (r, a) {
      r[a.categoryLevel2] = r[a.categoryLevel2] || []
      r[a.categoryLevel2]?.push(a)
      return r
    }, {}),
    // TODO: use this when department are correctly set in the database
    resultsByDepartment: dataEventsByRegion.reduce<EventByRegion>(function (
      r,
      a
    ) {
      r[a.countrySubdivisionNameLevel2] =
        r[a.countrySubdivisionNameLevel2] || []
      r[a.countrySubdivisionNameLevel2]?.push(a)
      return r
    },
    {})
  }

  const currencyExchangeRates =
    await liveRateRepository.getRunnerRatesForDefaultCurrency()

  const props = {
    ...(await serverSideTranslations(
      locale as string,
      [...defaultFrontLocales, 'event'],
      i18nConfig
    )),
    ...(page && { page }),
    ...(location && { location }),
    filters,
    typeOrDistanceKey: typeOrDistanceKey ?? null,
    parsedLocation,
    typeOrDistance,
    results,
    currencyExchangeRates,
    attributesToRetrieve: [
      'eventName',
      'eventCity',
      'eventLiveDistanceKm',
      'eventCountrySubdivisionDisplayCodeLevel2',
      'editionLiveLevel1CategoryKey',
      'eventSlug',
      'editionCalendarStatus',
      'eventLivePriceStartingFrom',
      'editionLiveStartDateTimestamp',
      'editionLiveEndDateTimestamp',
      'eventCoverImage',
      'editionLiveLevel2CategoryKey',
      'eventIsFeatured',
      'objectID',
      'eventCountry'
    ]
  }
  const serverState = await generateServerStateFromProps(props)

  // Since getStaticProps cannot handle queryParams, we still need to use getServerSideProps here
  // but with cache enabled
  res.setHeader('Cache-Control', 's-maxage=86400, stale-while-revalidate=3600')

  const initialResults = serverState?.initialResults[searchIndex]?.results?.[0]
  if (initialResults?.nbHits === 0) {
    return {
      redirect: {
        destination: `${getBaseUrl(locale)}/${typeOrDistance}`,
        permanent: true
      }
    }
  }
  if (
    typeof initialResults?.nbPages === 'number' &&
    page &&
    page > initialResults.nbPages
  ) {
    return {
      redirect: {
        destination: `${getBaseUrl(locale)}/${typeOrDistance}/${location}`,
        permanent: true
      }
    }
  }

  return {
    props: {
      ...props,
      serverState
    }
  }
}

export default Page
